import { type JSX, type ReactNode, useState } from 'react';
import { Link, generatePath } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { mdmIdQueryStr } from '../../common/constants/pathVariables.js';
import { CustomerOrderListFields } from '../../common/enums.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  approverContactMsg,
  companyMsg,
  contentMsg,
  customerMsg,
  imeiSlashSerialNumberMsg,
  newOrderMsg,
  noOrdersMsg,
  orderDateMsg,
  orderMsg,
  statusMsg,
  t,
  topicMsg,
  userMsg,
} from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { formatTimeStampToHHmm, formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils.js';
import { isValidSearchQuery, truncateText } from '../../common/utils/stringUtils.js';
import { CustomerOrderType } from '../../generated/api/models.js';
import { ConsolidatedListView } from '../ConsolidatedListView/ConsolidatedListView.js';
import { getCustomerOrderStatusInfo } from '../CustomerOrderDetails/CustomerOrderDetailsUtil.js';
import { getSearchFilterGroups } from '../CustomerOrderDetails/CustomerOrderListFilters';
import { Highlight } from '../Highlight/Highlight.js';
import { Status } from '../Status/Status.js';
import {
  DEFAULT_ITEMS_PER_PAGE,
  type DefaultListSearchParams,
  ListPagination,
  ListSearch,
  SearchFilters,
  Table,
  TableDescription,
  TableUrlParams,
} from '../Table/index.js';
import { setDefaultItemsPerPage } from '../Table/tableUtils.js';
import { getCustomerOrderType, getTopicText } from './customerOrderUtils';
import type { CLTableColumn } from '../../common/types/componentLibrary';
import type {
  CustomerOrderAggregationsResponse,
  CustomerOrderHeader,
  CustomerOrderSearchResponse,
} from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';

import './CustomerOrderListWithTable.scss';

const truncateTextMaxChars = 100;

const customerOrderAdditionalContentFields = [
  { name: 'selectedApproverName', getTitle: () => t.GOZ7(approverContactMsg) },
  { name: 'customerOrderContactName', getTitle: () => t.IY5V(customerMsg) },
  { name: 'deliveryOrderSerialNumbers', getTitle: () => t.OE45(imeiSlashSerialNumberMsg) },
];

const customerOrderAdditionalContentFieldNames = customerOrderAdditionalContentFields.map(field => field.name);

const getHighlightedAdditionalContentFields = (value: CustomerOrderSearchResponse, searchTerms: string[]) =>
  value.matchedFields
    ?.filter(matchedField => customerOrderAdditionalContentFieldNames.includes(matchedField))
    .map(matchedField => (
      <span key={matchedField}>
        {customerOrderAdditionalContentFields.find(field => field.name === matchedField)?.getTitle()}:&nbsp;
        <Highlight
          text={
            // @ts-ignore
            truncateText(value.result[matchedField], truncateTextMaxChars)
          }
          highlights={searchTerms}
        />
        &nbsp;
      </span>
    ));

export interface CustomerOrderListWithTableProps {
  basePath: string;
  legacySystem?: boolean;
  customerOrders?: CustomerOrderSearchResponse[];
  aggregations?: CustomerOrderAggregationsResponse;
  totalItems: number;
  loading?: boolean;
  searchQuery?: string;
  status?: string;
  authenticatedUser?: AuthenticatedUser;
  noFilters?: boolean;
  productType?: string;
  orderType?: string;
  showTopic?: boolean;
  isAdmin?: boolean;
}

export const generateCustomerOrderUrl = (path: string, orderId: string, mdmId?: string, legacySystem = false) => {
  const queryParams = new URLSearchParams(mdmIdQueryStr(path, mdmId).replace(/^\?/, ''));
  if (legacySystem) {
    queryParams.set(TableUrlParams.LEGACY, 'true');
  }
  const query = queryParams.toString();
  const queryString = query ? `?${query}` : '';
  return `${generatePath(path, { orderId })}${queryString}`;
};

const getCustomerOrderContactName = (customerOrderHeader: CustomerOrderHeader) => {
  const { deliveryOrderContactNames, deliveryOrderPurposeOfUses, customerOrderType, customerOrderContactName } =
    customerOrderHeader;
  if (customerOrderType === CustomerOrderType.NEW_ORDER) {
    return truncateText(
      (deliveryOrderContactNames?.length ?? 0) > 0 ? deliveryOrderContactNames : deliveryOrderPurposeOfUses,
      truncateTextMaxChars
    );
  }
  return customerOrderContactName ?? '';
};

export const CustomerOrderListWithTable = ({
  basePath,
  legacySystem,
  customerOrders,
  aggregations,
  loading,
  totalItems,
  searchQuery,
  status,
  authenticatedUser,
  productType,
  orderType,
  noFilters = false,
  showTopic,
  isAdmin = true,
}: CustomerOrderListWithTableProps): JSX.Element => {
  const searchTerms = searchQuery?.split(' ') || [];
  const searchParams = useSearchParams<DefaultListSearchParams>();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const columns: CLTableColumn[] = [
    {
      key: CustomerOrderListFields.CUSTOMER_ORDER_DISPLAY_ID,
      label: t.C001(orderMsg),
      sortable: true,
      width: '15%',
    },
    {
      key: CustomerOrderListFields.CREATED,
      label: t.V1HP(orderDateMsg),
      sortable: true,
      width: showTopic ? '12%' : '15%',
    },
    {
      key: CustomerOrderListFields.DELIVERY_ORDERS,
      label: t.NBNH(contentMsg),
      sortable: false,
      width: showTopic ? '18%' : '20%',
    },
    ...(showTopic
      ? [
          {
            key: CustomerOrderListFields.TOPIC,
            label: t.JJTS(topicMsg),
            sortable: false,
            width: '13%',
          } as CLTableColumn,
        ]
      : []),
    {
      key: CustomerOrderListFields.DELIVERY_ORDER_CONTACT_NAMES,
      label: t.LUZM(userMsg),
      sortable: false,
      width: showTopic ? '17%' : '20%',
    },
    {
      key: CustomerOrderListFields.STATUS,
      label: t.ASQT(statusMsg),
      sortable: true,
      width: '10%',
    },
    {
      key: CustomerOrderListFields.COMPANY,
      label: t.KJTS(companyMsg),
      sortable: false,
      width: showTopic ? '15%' : '20%',
    },
  ];

  const rows = customerOrders?.map(value => {
    const row: Record<string, ReactNode> = {
      [CustomerOrderListFields.CUSTOMER_ORDER_DISPLAY_ID]: (
        <>
          <Link
            to={generateCustomerOrderUrl(
              basePath,
              value.result.customerOrderDisplayId ?? '',
              value.result.accountMasterId,
              legacySystem
            )}
          >
            <Highlight text={value.result.customerOrderDisplayId ?? ''} highlights={searchTerms} />
            {isValidSearchQuery(searchQuery) &&
              value.matchedFields?.some(matchedField =>
                customerOrderAdditionalContentFieldNames.includes(matchedField)
              ) && (
                <div className="additional-field-content">
                  <strong>{t.NBNH(contentMsg)}: </strong>
                  {getHighlightedAdditionalContentFields(value, searchTerms)}
                </div>
              )}
          </Link>
          <TableDescription description={getCustomerOrderType(value?.result?.customerOrderType)} />
        </>
      ),
      [CustomerOrderListFields.CREATED]: (
        <TableDescription
          description={value.result.created ? formatTimeStampToHHmm(value.result.created) : ''}
          title={formatTimestampToDDMMYYYY(value.result.created)}
        />
      ),
      [CustomerOrderListFields.DELIVERY_ORDERS]: (
        <Highlight
          text={truncateText(value.result.deliveryOrders?.filter(Boolean), truncateTextMaxChars)}
          highlights={searchTerms}
        />
      ),
      [CustomerOrderListFields.DELIVERY_ORDER_CONTACT_NAMES]: (
        <>
          <Highlight text={getCustomerOrderContactName(value.result)} highlights={searchTerms} />
          <TableDescription description={value.result.customerOrderContactEmail ?? ''} />
        </>
      ),
      [CustomerOrderListFields.STATUS]: (
        <Status
          textAfterBadge={getCustomerOrderStatusInfo(value.result.status).text}
          color={getCustomerOrderStatusInfo(value.result.status).color}
          inTable={true}
        />
      ),
      [CustomerOrderListFields.COMPANY]: getCompanyName(authenticatedUser, value.result.accountMasterId),
    };

    if (showTopic === true) {
      row[CustomerOrderListFields.TOPIC] = (
        <TableDescription
          title={value.result.orderTopic ? getTopicText(value.result.orderTopic) : t.PTQ7(newOrderMsg)}
        />
      );
    }
    return row;
  });

  const shouldHideFilters = noFilters || !isAdmin;

  return (
    <div className={`of-customer-order-list ${dsClass.MARGIN_TOP_4}`}>
      <ConsolidatedListView
        authenticatedUser={authenticatedUser}
        listElement={
          <Table columns={columns} isLoading={loading} noItemsText={t.QFXU(noOrdersMsg)} rows={rows || []} />
        }
        listPagination={<ListPagination totalItems={totalItems} />}
        listSearch={
          <ListSearch
            filtersOpen={shouldHideFilters ? undefined : filtersOpen}
            setFiltersOpen={shouldHideFilters ? undefined : setFiltersOpen}
            onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)}
          />
        }
        searchFilters={
          shouldHideFilters ? undefined : (
            <SearchFilters
              displayActiveFiltersAsTiles={!filtersOpen}
              filterGroups={getSearchFilterGroups(aggregations, status, productType, orderType, isAdmin)}
              onModalClose={() => setFiltersOpen(false)}
            />
          )
        }
      />
    </div>
  );
};
